import { useEffect, useId, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useAuth } from "@/auth/AuthProvider";
import {
  BOND_FINAL_STEP_KEY,
  getBondSubmission,
  setBondBidResult,
  type BidResultInput,
  type BondSubmission,
} from "@/lib/brokerBonds";
import {
  bidExplanationRequired,
  bidGapPercent,
  ourBidFromOrder,
  THRESHOLD_PERCENT,
} from "@/lib/bidResult";
import type { AggregateSegment } from "@/lib/aggregateSegments";
import { formatBondCurrency } from "@/lib/bondValueDisplay";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { buttonClass } from "@/components/buttonStyles";
import { NumericField } from "@/components/NumericField";

// BRK-S402 — "Report bid result" from a bid segment of the account's aggregate
// bar: the wireframe's two-button modal (Aaron 2026-10-24), not the full bid
// result screen. The won flow, with its figures, stays on the submission.
//
//   • Client was low — `PUT …/bid-result {outcome: "low_bidder"}`
//     (PMS-S844): the bid is low-bidder-awaiting-award and keeps its reserve
//     until the P&P request.
//   • Not low, release it — the existing LOST path, the same PUT the bid result
//     screen sends. The next-lowest bid is optional here; once one is entered
//     the 10 % rule is unchanged (`bidExplanationRequired`, measured against
//     OUR bid as the order states it). The server decides: its 422s render
//     inline under the field they name, or as the line above the buttons.
//
// Nothing here says done. The caller hears `onRecorded` only with PMS's 2xx.

const FIELDS = ["nextLowestBidAmount", "explanation"] as const;

export type ReportedOutcome = "low_bidder" | "lost";

export function ReportBidResultDialog({
  segment,
  onClose,
  onRecorded,
}: {
  /** The bid segment being reported; null when closed. */
  segment: AggregateSegment | null;
  onClose: () => void;
  onRecorded: (
    segment: AggregateSegment,
    sent: ReportedOutcome,
    order: BondSubmission,
  ) => void;
}) {
  const { authedRequest } = useAuth();
  const titleId = useId();
  const explanationId = useId();
  const [nextLowest, setNextLowest] = useState<number | undefined>(undefined);
  const [explanation, setExplanation] = useState("");
  const [ourBid, setOurBid] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const orderId = segment?.orderId ?? null;

  // Fresh for every bid, and OUR bid read from the order — never from the
  // form, never from the capacity hold. A failed read leaves it null, which
  // means "not evaluated": a gap in our data must not refuse the answer.
  useEffect(() => {
    setNextLowest(undefined);
    setExplanation("");
    setOurBid(null);
    setError(null);
    setInvalid({});
    if (orderId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const order = await getBondSubmission(authedRequest, orderId);
        if (cancelled) return;
        setOurBid(
          order.bidResult?.ourBidAmount ??
            ourBidFromOrder(order.values?.[BOND_FINAL_STEP_KEY]),
        );
      } catch {
        if (!cancelled) setOurBid(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, orderId]);

  const gap =
    ourBid !== null && nextLowest !== undefined && nextLowest > 0
      ? bidGapPercent(ourBid, nextLowest)
      : null;
  const explanationRequired = bidExplanationRequired({
    outcome: "lost",
    ourBidAmount: ourBid,
    nextLowestBidAmount: nextLowest,
    // Whether the rule APPLIES, so the box shows before they type in it.
    explanation: null,
  });

  async function report(outcome: ReportedOutcome) {
    if (!segment) return;
    const body: BidResultInput =
      outcome === "low_bidder"
        ? { outcome }
        : {
            outcome,
            ...(nextLowest !== undefined
              ? { nextLowestBidAmount: nextLowest }
              : {}),
            ...(explanation.trim() ? { explanation: explanation.trim() } : {}),
          };
    setBusy(true);
    setError(null);
    setInvalid({});
    try {
      const order = await setBondBidResult(
        authedRequest,
        segment.orderId,
        body,
      );
      onRecorded(segment, outcome, order);
    } catch (err) {
      const anchored = anchorServerError(err, [...FIELDS]);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={segment !== null}
      onClose={busy ? undefined : onClose}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id={titleId}>Report bid result</DialogTitle>
      {segment && (
        <DialogContent>
          <div className="flex flex-col gap-4">
            <p className="text-sm font-medium text-brk-text">
              {segment.orderNumber} · Bid bond ·{" "}
              {formatBondCurrency(segment.amount)}
            </p>
            <p className="text-sm text-brk-dim">
              If the client wasn’t the low bidder, the bid comes off the
              aggregate right away. If they were low, it stays on at the bid
              amount until the performance and payment bond is issued.
            </p>

            <NumericField
              kind="currency"
              label="Next lowest bid"
              optional
              value={nextLowest}
              onChange={(value) => {
                setNextLowest(value);
                setInvalid((prev) => {
                  const next = { ...prev };
                  delete next.nextLowestBidAmount;
                  return next;
                });
              }}
              error={invalid.nextLowestBidAmount}
            />
            {gap !== null && (
              <p className="text-sm text-brk-dim">
                {gap > 0
                  ? `Your bid is ${String(gap)}% below the next-lowest bid.`
                  : gap < 0
                    ? `Your bid was ${String(Math.abs(gap))}% above the next-lowest bid.`
                    : "Your bid matched the next-lowest bid."}
              </p>
            )}

            {/* Shown only once the rule applies (D7), or once PMS has asked. */}
            {(explanationRequired || invalid.explanation) && (
              <div className="flex w-full flex-col gap-1.5 text-left">
                <label
                  htmlFor={explanationId}
                  className="text-sm font-medium text-brk-text"
                >
                  Explanation
                  <span aria-hidden="true" className="ml-1 text-brk-accent">
                    *
                  </span>
                </label>
                <p className="text-xs text-brk-dim">
                  More than {String(THRESHOLD_PERCENT)}% below the next-lowest
                  bid — tell us what you knew that the others didn’t.
                </p>
                <textarea
                  id={explanationId}
                  rows={3}
                  maxLength={2000}
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  aria-required
                  aria-invalid={invalid.explanation ? true : undefined}
                  className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
                />
                {invalid.explanation && (
                  <p role="alert" className="text-sm text-brk-danger">
                    {invalid.explanation}
                  </p>
                )}
              </div>
            )}

            {error && (
              <p role="alert" className="text-sm text-brk-danger">
                {error}
              </p>
            )}
          </div>
        </DialogContent>
      )}
      <DialogActions>
        {/* Stacked at 375 (the primary answer on top), in a row from `sm:`. */}
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className={buttonClass({ variant: "secondary" })}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void report("low_bidder")}
            disabled={busy}
            className={buttonClass({ variant: "secondary" })}
          >
            Client was low
          </button>
          <button
            type="button"
            onClick={() => void report("lost")}
            disabled={
              busy || (explanationRequired && explanation.trim() === "")
            }
            className={buttonClass()}
          >
            Not low, release it
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
}
